import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, isVisitExpired, type VisitRow } from "@/lib/api";

const statusOptions = ["", "pending", "approved", "denied", "cancelled", "archived"] as const;

export default function AdminDashboard() {
  const [status, setStatus] = useState<string>("pending");
  const [q, setQ] = useState("");
  const [page, setPage] = useState(1);
  const [data, setData] = useState<{ rows: VisitRow[]; total: number; pageSize: number } | null>(null);
  const [loading, setLoading] = useState(false);

  async function load() {
    setLoading(true);
    try {
      const r = await api.listVisits({
        status,
        q,
        page: String(page),
        pageSize: "25",
      });
      setData(r);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status, page]);

  function exportUrl(format: "csv" | "ics") {
    const params = new URLSearchParams({ status, q, format });
    return `/api/admin/visits/export?${params.toString()}`;
  }

  return (
    <div>
      <div className="flex flex-wrap items-end gap-3 mb-4">
        <div>
          <label className="label">Status</label>
          <select className="select" value={status}
                  onChange={(e) => { setStatus(e.target.value); setPage(1); }}>
            {statusOptions.map((s) => (
              <option key={s} value={s}>{s || "all"}</option>
            ))}
          </select>
        </div>
        <div className="grow min-w-[200px]">
          <label className="label">Search</label>
          <input className="input" placeholder="name, email, organization"
                 value={q} onChange={(e) => setQ(e.target.value)}
                 onKeyDown={(e) => { if (e.key === "Enter") load(); }} />
        </div>
        <button className="btn-secondary" onClick={load}>Search</button>
        <a className="btn-secondary" href={exportUrl("csv")}>Export CSV</a>
        <a className="btn-secondary" href={exportUrl("ics")}>Export .ics</a>
      </div>

      <div className="card overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-stone-100 text-left">
            <tr>
              <th className="px-3 py-2">#</th>
              <th className="px-3 py-2">Submitted</th>
              <th className="px-3 py-2">Contact</th>
              <th className="px-3 py-2">Organization</th>
              <th className="px-3 py-2">Attendees</th>
              <th className="px-3 py-2">Status</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={7} className="px-3 py-6 text-museum-muted">Loading…</td></tr>
            )}
            {!loading && data?.rows.length === 0 && (
              <tr><td colSpan={7} className="px-3 py-6 text-museum-muted">No requests match.</td></tr>
            )}
            {!loading && data?.rows.map((r) => (
              <tr key={r.id} className="border-t border-stone-200">
                <td className="px-3 py-2">{r.id}</td>
                <td className="px-3 py-2 text-museum-muted">
                  {new Date(r.created_at + "Z").toLocaleString()}
                </td>
                <td className="px-3 py-2">
                  <div className="font-medium">{r.contact_name}</div>
                  <div className="text-xs text-museum-muted">{r.contact_email}</div>
                </td>
                <td className="px-3 py-2">{r.organization ?? "—"}</td>
                <td className="px-3 py-2">{r.attendees_count ?? "—"}</td>
                <td className="px-3 py-2">
                  <div className="flex flex-wrap gap-1">
                    <StatusBadge status={r.status} />
                    {isVisitExpired(r) && (
                      <span className="inline-block px-2 py-0.5 rounded text-xs bg-amber-100 text-amber-800">Expired</span>
                    )}
                    {r.archived_at && (
                      <span className="inline-block px-2 py-0.5 rounded text-xs bg-stone-200 text-stone-700">Archived</span>
                    )}
                  </div>
                </td>
                <td className="px-3 py-2 text-right">
                  <Link to={`/admin/visits/${r.id}`} className="underline">Review</Link>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {data && data.total > data.pageSize && (
        <div className="flex justify-between items-center mt-3 text-sm">
          <div>{data.total} total</div>
          <div className="flex gap-2">
            <button className="btn-secondary" disabled={page === 1}
                    onClick={() => setPage((p) => p - 1)}>← Prev</button>
            <button className="btn-secondary"
                    disabled={page * data.pageSize >= data.total}
                    onClick={() => setPage((p) => p + 1)}>Next →</button>
          </div>
        </div>
      )}
    </div>
  );
}

function StatusBadge({ status }: { status: VisitRow["status"] }) {
  const cls: Record<VisitRow["status"], string> = {
    pending:   "bg-amber-100 text-amber-800",
    approved:  "bg-green-100 text-green-800",
    denied:    "bg-stone-200 text-stone-700",
    cancelled: "bg-stone-200 text-stone-700",
  };
  return <span className={`inline-block px-2 py-0.5 rounded text-xs ${cls[status]}`}>{status}</span>;
}
